import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import {
    getAllEvaluationsRequest,
    getEvaluationsByTypeRequest,
    createEvaluationRequest,
    updateEvaluationRequest,
    deleteEvaluationRequest,
    changeEvaluationStatusRequest,
    updateEvaluation,
} from "../../redux/actions/evaluationActions";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import "./EvaluationsManagement.css";
import EvaluationSheet from "./EvaluationSheet";
import {
    Pencil,
    Trash2,
    Plus,
    X,
    Layers3,
    ListChecks,
    SlidersHorizontal,
    Eye,
} from "lucide-react";

/* ──────────────────────────────────────────────
   Helpers
────────────────────────────────────────────── */
const emptyQuestion = (position = 1) => ({
    label: "",
    questionType: "RATING",
    targetRole: "COLLABORATOR",
    position,
    required: true,
    active: true,
    options: [],
});

const emptyChoiceOption = (position = 1) => ({
    label: "",
    value: `option_${position}`,
    position,
    active: true,
    scoreValue: "",
});

const emptySection = (position = 1) => ({
    code: "",
    title: "",
    description: "",
    position,
    weight: 0,
    enabled: true,
    questions: [emptyQuestion(1)],
});

const emptyScaleOption = (position = 1) => ({
    label: "",
    value: position,
    position,
});

const defaultFormData = {
    code: "",
    name: "",
    description: "",
    typeCode: "HOT",
    status: "ACTIVE",
    settings: {
        visibilityMode: "COLLABORATOR",
        managerPercentage: 0,
        collaboratorPercentage: 100,
        evaluateTrainingObjectives: false,
        evaluateRequestObjectives: false,
        allowFreeComment: true,
        anonymous: false,
        minScore: 1,
        maxScore: 5,
    },
    sections: [emptySection(1)],
    scaleOptions: [
        { label: "Très bon", value: 4, position: 1 },
        { label: "Bon", value: 3, position: 2 },
        { label: "Moyen", value: 2, position: 3 },
        { label: "Mauvais", value: 1, position: 4 },
    ],
};

/* ──────────────────────────────────────────────
   Preview Modal
────────────────────────────────────────────── */
const EvaluationPreviewModal = ({ isOpen, onClose, evaluation, t }) => {
    if (!isOpen || !evaluation) return null;

    return (
        <div className="em-overlay" onClick={onClose}>
            <div
                className="em-modal em-modal-lg"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="em-modal-header">
                    <h3>{t("Aperçu de l'évaluation")}</h3>
                    <button
                        className="em-modal-close"
                        onClick={onClose}
                        type="button"
                    >
                        ✕
                    </button>
                </div>

                <div className="em-form">
                    <EvaluationSheet
                        evaluation={evaluation}
                        readOnly={true}
                        showComment={true}
                    />
                </div>
            </div>
        </div>
    );
};/* ──────────────────────────────────────────────
   CRUD Modal
────────────────────────────────────────────── */
const EvaluationModal = ({
                             isOpen,
                             onClose,
                             onSubmit,
                             editingEvaluation,
                             loading,
                         }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState(defaultFormData);
    const [activeTab, setActiveTab] = useState("general");

    useEffect(() => {
        if (editingEvaluation) {
            setFormData({
                code: editingEvaluation.code || "",
                name: editingEvaluation.name || "",
                description: editingEvaluation.description || "",
                typeCode: editingEvaluation.typeCode || "HOT",
                status: editingEvaluation.status || "ACTIVE",
                settings: {
                    visibilityMode:
                        editingEvaluation.settings?.visibilityMode || "COLLABORATOR",
                    managerPercentage:
                        editingEvaluation.settings?.managerPercentage ?? 0,
                    collaboratorPercentage:
                        editingEvaluation.settings?.collaboratorPercentage ?? 100,
                    evaluateTrainingObjectives:
                        editingEvaluation.settings?.evaluateTrainingObjectives ?? false,
                    evaluateRequestObjectives:
                        editingEvaluation.settings?.evaluateRequestObjectives ?? false,
                    allowFreeComment:
                        editingEvaluation.settings?.allowFreeComment ?? true,
                    anonymous: editingEvaluation.settings?.anonymous ?? false,
                    minScore: editingEvaluation.settings?.minScore ?? 1,
                    maxScore: editingEvaluation.settings?.maxScore ?? 5,
                },
                sections:
                    editingEvaluation.sections?.length > 0
                        ? editingEvaluation.sections.map((section, sIndex) => ({
                            code: section.code || "",
                            title: section.title || "",
                            description: section.description || "",
                            position: section.position ?? sIndex + 1,
                            weight: section.weight ?? 0,
                            enabled: section.enabled ?? true,
                            questions:
                                section.questions?.length > 0
                                    ? section.questions.map((question, qIndex) => ({
                                        label: question.label || "",
                                        questionType: question.questionType || "RATING",
                                        targetRole: question.targetRole || "COLLABORATOR",
                                        position: question.position ?? qIndex + 1,
                                        required: question.required ?? true,
                                        active: question.active ?? true,
                                        options:
                                            question.options?.length > 0
                                                ? question.options.map((option, optionIndex) => ({
                                                    label: option.label || "",
                                                    value: option.value || `option_${optionIndex + 1}`,
                                                    position: option.position ?? optionIndex + 1,
                                                    active: option.active ?? true,
                                                    scoreValue: option.scoreValue ?? "",
                                                }))
                                                : [],
                                    }))
                                    : [emptyQuestion(1)],
                        }))
                        : [emptySection(1)],
                scaleOptions:
                    editingEvaluation.scaleOptions?.length > 0
                        ? editingEvaluation.scaleOptions.map((option, index) => ({
                            label: option.label || "",
                            value: option.value ?? index + 1,
                            position: option.position ?? index + 1,
                        }))
                        : defaultFormData.scaleOptions,
            });
        } else {
            setFormData(defaultFormData);
        }

        setActiveTab("general");
    }, [editingEvaluation, isOpen]);

    if (!isOpen) return null;

    const handleRootChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                [name]: type === "checkbox" ? checked : value,
            },
        }));
    };

    const handleSettingsNumberChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                [name]: value === "" ? "" : Number(value),
            },
        }));
    };

    const handleSectionChange = (sectionIndex, field, value) => {
        setFormData((prev) => {
            const sections = [...prev.sections];
            sections[sectionIndex] = {
                ...sections[sectionIndex],
                [field]: value,
            };
            return { ...prev, sections };
        });
    };

    const handleQuestionChange = (sectionIndex, questionIndex, field, value) => {
        setFormData((prev) => {
            const sections = [...prev.sections];
            const questions = [...sections[sectionIndex].questions];
            questions[questionIndex] = {
                ...questions[questionIndex],
                [field]: value,
            };
            sections[sectionIndex] = {
                ...sections[sectionIndex],
                questions,
            };
            return { ...prev, sections };
        });
    };

    const handleQuestionTypeChange = (sectionIndex, questionIndex, questionType) => {
        setFormData((prev) => {
            const sections = [...prev.sections];
            const questions = [...sections[sectionIndex].questions];
            const currentQuestion = questions[questionIndex];
            questions[questionIndex] = {
                ...currentQuestion,
                questionType,
                options:
                    questionType === "CHOICE"
                        ? (currentQuestion.options?.length ? currentQuestion.options : [emptyChoiceOption(1), emptyChoiceOption(2)])
                        : [],
            };
            sections[sectionIndex] = {
                ...sections[sectionIndex],
                questions,
            };
            return { ...prev, sections };
        });
    };

    const handleQuestionOptionChange = (sectionIndex, questionIndex, optionIndex, field, value) => {
        setFormData((prev) => {
            const sections = [...prev.sections];
            const questions = [...sections[sectionIndex].questions];
            const options = [...(questions[questionIndex].options || [])];
            options[optionIndex] = {
                ...options[optionIndex],
                [field]: value,
            };
            questions[questionIndex] = {
                ...questions[questionIndex],
                options,
            };
            sections[sectionIndex] = {
                ...sections[sectionIndex],
                questions,
            };
            return { ...prev, sections };
        });
    };

    const handleScaleOptionChange = (index, field, value) => {
        setFormData((prev) => {
            const scaleOptions = [...prev.scaleOptions];
            scaleOptions[index] = {
                ...scaleOptions[index],
                [field]: value,
            };
            return { ...prev, scaleOptions };
        });
    };

    const addSection = () => {
        setFormData((prev) => ({
            ...prev,
            sections: [...prev.sections, emptySection(prev.sections.length + 1)],
        }));
    };

    const removeSection = (sectionIndex) => {
        setFormData((prev) => {
            const sections = prev.sections.filter((_, i) => i !== sectionIndex);
            return {
                ...prev,
                sections: sections.map((section, index) => ({
                    ...section,
                    position: index + 1,
                })),
            };
        });
    };

    const addQuestion = (sectionIndex) => {
        setFormData((prev) => {
            const sections = [...prev.sections];
            const questions = [...sections[sectionIndex].questions];
            questions.push(emptyQuestion(questions.length + 1));
            sections[sectionIndex] = { ...sections[sectionIndex], questions };
            return { ...prev, sections };
        });
    };

    const removeQuestion = (sectionIndex, questionIndex) => {
        setFormData((prev) => {
            const sections = [...prev.sections];
            const questions = sections[sectionIndex].questions
                .filter((_, i) => i !== questionIndex)
                .map((question, index) => ({
                    ...question,
                    position: index + 1,
                }));

            sections[sectionIndex] = {
                ...sections[sectionIndex],
                questions: questions.length ? questions : [emptyQuestion(1)],
            };

            return { ...prev, sections };
        });
    };

    const addQuestionOption = (sectionIndex, questionIndex) => {
        setFormData((prev) => {
            const sections = [...prev.sections];
            const questions = [...sections[sectionIndex].questions];
            const options = [...(questions[questionIndex].options || [])];
            options.push(emptyChoiceOption(options.length + 1));
            questions[questionIndex] = {
                ...questions[questionIndex],
                options,
            };
            sections[sectionIndex] = {
                ...sections[sectionIndex],
                questions,
            };
            return { ...prev, sections };
        });
    };

    const removeQuestionOption = (sectionIndex, questionIndex, optionIndex) => {
        setFormData((prev) => {
            const sections = [...prev.sections];
            const questions = [...sections[sectionIndex].questions];
            const options = (questions[questionIndex].options || [])
                .filter((_, index) => index !== optionIndex)
                .map((option, index) => ({
                    ...option,
                    position: index + 1,
                }));
            questions[questionIndex] = {
                ...questions[questionIndex],
                options,
            };
            sections[sectionIndex] = {
                ...sections[sectionIndex],
                questions,
            };
            return { ...prev, sections };
        });
    };

    const addScaleOption = () => {
        setFormData((prev) => ({
            ...prev,
            scaleOptions: [
                ...prev.scaleOptions,
                emptyScaleOption(prev.scaleOptions.length + 1),
            ],
        }));
    };

    const removeScaleOption = (index) => {
        setFormData((prev) => ({
            ...prev,
            scaleOptions: prev.scaleOptions
                .filter((_, i) => i !== index)
                .map((option, idx) => ({
                    ...option,
                    position: idx + 1,
                })),
        }));
    };

    const validateForm = () => {
        if (!formData.code?.trim()) {
            Swal.fire({ icon: "warning", text: t("Le code est obligatoire") });
            setActiveTab("general");
            return false;
        }

        if (!formData.name?.trim()) {
            Swal.fire({ icon: "warning", text: t("Le nom est obligatoire") });
            setActiveTab("general");
            return false;
        }

        if (!formData.sections?.length) {
            Swal.fire({
                icon: "warning",
                text: t("Au moins une section est obligatoire"),
            });
            setActiveTab("sections");
            return false;
        }

        const enabledSections = formData.sections.filter((s) => !!s.enabled);
        if (!enabledSections.length) {
            Swal.fire({
                icon: "warning",
                text: t("Au moins une section activée est obligatoire"),
            });
            setActiveTab("sections");
            return false;
        }

        const totalWeight = enabledSections.reduce(
            (sum, section) => sum + Number(section.weight || 0),
            0
        );
        if (totalWeight !== 100) {
            Swal.fire({
                icon: "warning",
                text: t("La somme des pondérations des sections activées doit être égale à 100"),
            });
            setActiveTab("sections");
            return false;
        }

        for (const section of enabledSections) {
            if (!section.code?.trim() || !section.title?.trim()) {
                Swal.fire({
                    icon: "warning",
                    text: t("Chaque section doit avoir un code et un titre"),
                });
                setActiveTab("sections");
                return false;
            }

            if (!section.questions?.length) {
                Swal.fire({
                    icon: "warning",
                    text: t("Chaque section activée doit contenir au moins une question"),
                });
                setActiveTab("sections");
                return false;
            }

            for (const question of section.questions) {
                if (!question.label?.trim()) {
                    Swal.fire({
                        icon: "warning",
                        text: t("Chaque question doit avoir un libellé"),
                    });
                    setActiveTab("sections");
                    return false;
                }

                if (question.questionType === "CHOICE") {
                    const activeOptions = (question.options || []).filter((option) => !!option.active);
                    const optionPositions = new Set();

                    if (activeOptions.length < 2) {
                        Swal.fire({
                            icon: "warning",
                            text: t("Chaque question CHOICE doit avoir au moins 2 options actives"),
                        });
                        setActiveTab("sections");
                        return false;
                    }

                    for (const option of question.options || []) {
                        if (!option.label?.trim()) {
                            Swal.fire({
                                icon: "warning",
                                text: t("Chaque option CHOICE doit avoir un libelle"),
                            });
                            setActiveTab("sections");
                            return false;
                        }

                        if (optionPositions.has(Number(option.position))) {
                            Swal.fire({
                                icon: "warning",
                                text: t("Les positions des options CHOICE doivent etre uniques"),
                            });
                            setActiveTab("sections");
                            return false;
                        }
                        optionPositions.add(Number(option.position));
                    }
                }
            }
        }

        if (!formData.scaleOptions?.length || formData.scaleOptions.length < 2) {
            Swal.fire({
                icon: "warning",
                text: t("L'échelle de notation doit contenir au moins 2 valeurs"),
            });
            setActiveTab("scale");
            return false;
        }

        if (formData.settings.visibilityMode === "BOTH") {
            const m = Number(formData.settings.managerPercentage || 0);
            const c = Number(formData.settings.collaboratorPercentage || 0);
            if (m + c !== 100) {
                Swal.fire({
                    icon: "warning",
                    text: t("Manager + Collaborateur doit être égal à 100"),
                });
                setActiveTab("settings");
                return false;
            }
        }

        return true;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const payload = {
            ...formData,
            code: formData.code.trim(),
            name: formData.name.trim(),
            description: formData.description?.trim() || "",
            settings: {
                ...formData.settings,
                managerPercentage: Number(formData.settings.managerPercentage || 0),
                collaboratorPercentage: Number(
                    formData.settings.collaboratorPercentage || 0
                ),
                minScore: Number(formData.settings.minScore || 0),
                maxScore: Number(formData.settings.maxScore || 0),
            },
            sections: formData.sections.map((section, sIndex) => ({
                ...section,
                code: section.code.trim(),
                title: section.title.trim(),
                description: section.description?.trim() || "",
                position: sIndex + 1,
                weight: Number(section.weight || 0),
                questions: section.questions.map((question, qIndex) => ({
                    ...question,
                    label: question.label.trim(),
                    position: qIndex + 1,
                    options:
                        question.questionType === "CHOICE"
                            ? (question.options || []).map((option, optionIndex) => ({
                                ...option,
                                label: option.label.trim(),
                                value: option.value?.trim() || option.label.trim(),
                                position: optionIndex + 1,
                                active: option.active ?? true,
                                scoreValue: option.scoreValue === "" ? null : Number(option.scoreValue),
                            }))
                            : [],
                })),
            })),
            scaleOptions: formData.scaleOptions.map((option, index) => ({
                ...option,
                label: option.label.trim(),
                value: Number(option.value || 0),
                position: index + 1,
            })),
        };

        onSubmit(payload);
    };

    return (
        <div className="em-overlay" onClick={onClose}>
            <div className="em-modal em-modal-xl" onClick={(e) => e.stopPropagation()}>
                <div className="em-modal-header">
                    <h3>
                        {editingEvaluation
                            ? t("Modifier l'évaluation")
                            : t("Ajouter une évaluation")}
                    </h3>
                    <button className="em-modal-close" onClick={onClose} type="button">
                        ✕
                    </button>
                </div>

                <form className="em-form" onSubmit={handleSubmit}>
                    <div className="em-tabs">
                        <button
                            type="button"
                            className={`em-tab ${activeTab === "general" ? "active" : ""}`}
                            onClick={() => setActiveTab("general")}
                        >
                            {t("Général")}
                        </button>
                        <button
                            type="button"
                            className={`em-tab ${activeTab === "settings" ? "active" : ""}`}
                            onClick={() => setActiveTab("settings")}
                        >
                            {t("Paramètres")}
                        </button>
                        <button
                            type="button"
                            className={`em-tab ${activeTab === "sections" ? "active" : ""}`}
                            onClick={() => setActiveTab("sections")}
                        >
                            {t("Sections & Questions")}
                        </button>
                        <button
                            type="button"
                            className={`em-tab ${activeTab === "scale" ? "active" : ""}`}
                            onClick={() => setActiveTab("scale")}
                        >
                            {t("Échelle")}
                        </button>
                    </div>

                    {activeTab === "general" && (
                        <div className="em-grid">
                            <div className="em-field">
                                <label>{t("Code")} *</label>
                                <input
                                    name="code"
                                    value={formData.code}
                                    onChange={handleRootChange}
                                    placeholder="EVAL-HOT-001"
                                    maxLength={100}
                                />
                            </div>

                            <div className="em-field">
                                <label>{t("Nom")} *</label>
                                <input
                                    name="name"
                                    value={formData.name}
                                    onChange={handleRootChange}
                                    placeholder={t("Nom de l'évaluation")}
                                    maxLength={200}
                                />
                            </div>

                            <div className="em-field">
                                <label>{t("Type")}</label>
                                <select
                                    name="typeCode"
                                    value={formData.typeCode}
                                    onChange={handleRootChange}
                                >
                                    <option value="HOT">à chaud</option>
                                    <option value="COLD">à froid</option>
                                    {/*<option value="TECHNICAL">TECHNICAL</option>*/}
                                    {/*<option value="MANAGERIAL">MANAGERIAL</option>*/}
                                    {/*<option value="CUSTOM">CUSTOM</option>*/}
                                </select>
                            </div>

                            <div className="em-field">
                                <label>{t("Statut")}</label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleRootChange}
                                >
                                    <option value="ACTIVE">Active</option>
                                    <option value="INACTIVE">Inactive</option>
                                    {/*<option value="ARCHIVED">ARCHIVED</option>*/}
                                </select>
                            </div>

                            <div className="em-field em-full">
                                <label>{t("Description")}</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleRootChange}
                                    rows={4}
                                    placeholder={t("Description de l'évaluation...")}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === "settings" && (
                        <div className="em-grid">
                            <div className="em-field">
                                <label>{t("Mode de visibilité")}</label>
                                <select
                                    name="visibilityMode"
                                    value={formData.settings.visibilityMode}
                                    onChange={handleSettingsChange}
                                >
                                    <option value="MANAGER">MANAGER</option>
                                    <option value="COLLABORATOR">COLLABORATOR</option>
                                    <option value="BOTH">BOTH</option>
                                </select>
                            </div>

                            <div className="em-field">
                                <label>{t("Score minimum")}</label>
                                <input
                                    type="number"
                                    name="minScore"
                                    value={formData.settings.minScore}
                                    onChange={handleSettingsNumberChange}
                                />
                            </div>

                            <div className="em-field">
                                <label>{t("Pourcentage manager")}</label>
                                <input
                                    type="number"
                                    name="managerPercentage"
                                    value={formData.settings.managerPercentage}
                                    onChange={handleSettingsNumberChange}
                                />
                            </div>

                            <div className="em-field">
                                <label>{t("Pourcentage collaborateur")}</label>
                                <input
                                    type="number"
                                    name="collaboratorPercentage"
                                    value={formData.settings.collaboratorPercentage}
                                    onChange={handleSettingsNumberChange}
                                />
                            </div>

                            <div className="em-field">
                                <label>{t("Score maximum")}</label>
                                <input
                                    type="number"
                                    name="maxScore"
                                    value={formData.settings.maxScore}
                                    onChange={handleSettingsNumberChange}
                                />
                            </div>

                            <div className="em-field em-full">
                                <div className="em-check-grid">
                                    <label className="em-check-label">
                                        <input
                                            type="checkbox"
                                            name="evaluateTrainingObjectives"
                                            checked={!!formData.settings.evaluateTrainingObjectives}
                                            onChange={handleSettingsChange}
                                        />
                                        {t("Évaluer les objectifs de formation")}
                                    </label>

                                    <label className="em-check-label">
                                        <input
                                            type="checkbox"
                                            name="evaluateRequestObjectives"
                                            checked={!!formData.settings.evaluateRequestObjectives}
                                            onChange={handleSettingsChange}
                                        />
                                        {t("Évaluer les objectifs de demande")}
                                    </label>

                                    <label className="em-check-label">
                                        <input
                                            type="checkbox"
                                            name="allowFreeComment"
                                            checked={!!formData.settings.allowFreeComment}
                                            onChange={handleSettingsChange}
                                        />
                                        {t("Autoriser le commentaire libre")}
                                    </label>

                                    <label className="em-check-label">
                                        <input
                                            type="checkbox"
                                            name="anonymous"
                                            checked={!!formData.settings.anonymous}
                                            onChange={handleSettingsChange}
                                        />
                                        {t("Mode anonyme")}
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === "sections" && (
                        <div className="em-editor-block">
                            <div className="em-editor-head">
                                <h4>
                                    <Layers3 size={16} /> {t("Sections")}
                                </h4>
                                <button
                                    type="button"
                                    className="em-btn em-btn-primary"
                                    onClick={addSection}
                                >
                                    <Plus size={14} /> {t("Ajouter une section")}
                                </button>
                            </div>

                            {formData.sections.map((section, sectionIndex) => (
                                <div className="em-card" key={sectionIndex}>
                                    <div className="em-card-head">
                                        <div className="em-card-title">
                                            {t("Section")} #{sectionIndex + 1}
                                        </div>
                                        <button
                                            type="button"
                                            className="em-link-btn danger"
                                            onClick={() => removeSection(sectionIndex)}
                                            disabled={formData.sections.length === 1}
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>

                                    <div className="em-grid">
                                        <div className="em-field">
                                            <label>{t("Code")} *</label>
                                            <input
                                                value={section.code}
                                                onChange={(e) =>
                                                    handleSectionChange(sectionIndex, "code", e.target.value)
                                                }
                                                placeholder="FORMATION_PEDAGOGIE"
                                            />
                                        </div>

                                        <div className="em-field">
                                            <label>{t("Titre")} *</label>
                                            <input
                                                value={section.title}
                                                onChange={(e) =>
                                                    handleSectionChange(sectionIndex, "title", e.target.value)
                                                }
                                                placeholder={t("Titre de la section")}
                                            />
                                        </div>

                                        <div className="em-field">
                                            <label>{t("Position")}</label>
                                            <input
                                                type="number"
                                                value={section.position}
                                                onChange={(e) =>
                                                    handleSectionChange(
                                                        sectionIndex,
                                                        "position",
                                                        Number(e.target.value)
                                                    )
                                                }
                                            />
                                        </div>

                                        <div className="em-field">
                                            <label>{t("Pondération %")}</label>
                                            <input
                                                type="number"
                                                value={section.weight}
                                                onChange={(e) =>
                                                    handleSectionChange(
                                                        sectionIndex,
                                                        "weight",
                                                        Number(e.target.value)
                                                    )
                                                }
                                            />
                                        </div>

                                        <div className="em-field em-full">
                                            <label>{t("Description")}</label>
                                            <textarea
                                                rows={2}
                                                value={section.description}
                                                onChange={(e) =>
                                                    handleSectionChange(
                                                        sectionIndex,
                                                        "description",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </div>

                                        <div className="em-field em-full">
                                            <label className="em-check-label">
                                                <input
                                                    type="checkbox"
                                                    checked={!!section.enabled}
                                                    onChange={(e) =>
                                                        handleSectionChange(
                                                            sectionIndex,
                                                            "enabled",
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                                {t("Section activée")}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="em-subeditor">
                                        <div className="em-editor-head">
                                            <h4>
                                                <ListChecks size={16} /> {t("Questions")}
                                            </h4>
                                            <button
                                                type="button"
                                                className="em-btn em-btn-outline"
                                                onClick={() => addQuestion(sectionIndex)}
                                            >
                                                <Plus size={14} /> {t("Ajouter une question")}
                                            </button>
                                        </div>

                                        {section.questions.map((question, questionIndex) => (
                                            <div className="em-question-row" key={questionIndex}>
                                                <div className="em-grid">
                                                    <div className="em-field em-full">
                                                        <label>{t("Libellé")} *</label>
                                                        <input
                                                            value={question.label}
                                                            onChange={(e) =>
                                                                handleQuestionChange(
                                                                    sectionIndex,
                                                                    questionIndex,
                                                                    "label",
                                                                    e.target.value
                                                                )
                                                            }
                                                            placeholder={t("Question...")}
                                                        />
                                                    </div>

                                                    <div className="em-field">
                                                        <label>{t("Type")}</label>
                                                        <select
                                                            value={question.questionType}
                                                            onChange={(e) =>
                                                                handleQuestionTypeChange(
                                                                    sectionIndex,
                                                                    questionIndex,
                                                                    e.target.value
                                                                )
                                                            }
                                                        >
                                                            <option value="RATING">RATING</option>
                                                            <option value="TEXT">TEXT</option>
                                                            <option value="BOOLEAN">BOOLEAN</option>
                                                            <option value="CHOICE">CHOICE</option>
                                                        </select>
                                                    </div>

                                                    <div className="em-field">
                                                        <label>{t("Rôle cible")}</label>
                                                        <select
                                                            value={question.targetRole}
                                                            onChange={(e) =>
                                                                handleQuestionChange(
                                                                    sectionIndex,
                                                                    questionIndex,
                                                                    "targetRole",
                                                                    e.target.value
                                                                )
                                                            }
                                                        >
                                                            <option value="MANAGER">MANAGER</option>
                                                            <option value="COLLABORATOR">
                                                                COLLABORATOR
                                                            </option>
                                                            <option value="RESPONSABLE_FORMATION">
                                                                RESPONSABLE_FORMATION
                                                            </option>
                                                            <option value="ALL">ALL</option>
                                                        </select>
                                                    </div>

                                                    <div className="em-field">
                                                        <label>{t("Position")}</label>
                                                        <input
                                                            type="number"
                                                            value={question.position}
                                                            onChange={(e) =>
                                                                handleQuestionChange(
                                                                    sectionIndex,
                                                                    questionIndex,
                                                                    "position",
                                                                    Number(e.target.value)
                                                                )
                                                            }
                                                        />
                                                    </div>

                                                    <div className="em-field">
                                                        <label>{t("Actions")}</label>
                                                        <div className="em-inline-actions">
                                                            <label className="em-check-label">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!question.required}
                                                                    onChange={(e) =>
                                                                        handleQuestionChange(
                                                                            sectionIndex,
                                                                            questionIndex,
                                                                            "required",
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                />
                                                                {t("Obligatoire")}
                                                            </label>

                                                            <label className="em-check-label">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!question.active}
                                                                    onChange={(e) =>
                                                                        handleQuestionChange(
                                                                            sectionIndex,
                                                                            questionIndex,
                                                                            "active",
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                />
                                                                {t("Active")}
                                                            </label>

                                                            <button
                                                                type="button"
                                                                className="em-link-btn danger"
                                                                onClick={() =>
                                                                    removeQuestion(sectionIndex, questionIndex)
                                                                }
                                                            >
                                                                <Trash2 size={15} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                {question.questionType === "CHOICE" && (
                                                    <div className="em-choice-options">
                                                        <div className="em-editor-head em-editor-head--compact">
                                                            <h4>{t("Options CHOICE")}</h4>
                                                            <button
                                                                type="button"
                                                                className="em-btn em-btn-outline"
                                                                onClick={() => addQuestionOption(sectionIndex, questionIndex)}
                                                            >
                                                                <Plus size={14} /> {t("Ajouter une option")}
                                                            </button>
                                                        </div>

                                                        {(question.options || []).map((option, optionIndex) => (
                                                            <div className="em-choice-option-row" key={optionIndex}>
                                                                <div className="em-field">
                                                                    <label>{t("Libelle")}</label>
                                                                    <input
                                                                        value={option.label}
                                                                        onChange={(e) =>
                                                                            handleQuestionOptionChange(
                                                                                sectionIndex,
                                                                                questionIndex,
                                                                                optionIndex,
                                                                                "label",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        placeholder={t("Option...")}
                                                                    />
                                                                </div>

                                                                <div className="em-field">
                                                                    <label>{t("Valeur")}</label>
                                                                    <input
                                                                        value={option.value}
                                                                        onChange={(e) =>
                                                                            handleQuestionOptionChange(
                                                                                sectionIndex,
                                                                                questionIndex,
                                                                                optionIndex,
                                                                                "value",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        placeholder="option_1"
                                                                    />
                                                                </div>

                                                                <div className="em-field">
                                                                    <label>{t("Position")}</label>
                                                                    <input
                                                                        type="number"
                                                                        value={option.position}
                                                                        onChange={(e) =>
                                                                            handleQuestionOptionChange(
                                                                                sectionIndex,
                                                                                questionIndex,
                                                                                optionIndex,
                                                                                "position",
                                                                                Number(e.target.value)
                                                                            )
                                                                        }
                                                                    />
                                                                </div>

                                                                <label className="em-check-label em-choice-active">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!option.active}
                                                                        onChange={(e) =>
                                                                            handleQuestionOptionChange(
                                                                                sectionIndex,
                                                                                questionIndex,
                                                                                optionIndex,
                                                                                "active",
                                                                                e.target.checked
                                                                            )
                                                                        }
                                                                    />
                                                                    {t("Active")}
                                                                </label>

                                                                <button
                                                                    type="button"
                                                                    className="em-link-btn danger"
                                                                    onClick={() => removeQuestionOption(sectionIndex, questionIndex, optionIndex)}
                                                                    disabled={(question.options || []).length <= 2}
                                                                >
                                                                    <Trash2 size={15} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {activeTab === "scale" && (
                        <div className="em-editor-block">
                            <div className="em-editor-head">
                                <h4>
                                    <SlidersHorizontal size={16} /> {t("Échelle de notation")}
                                </h4>
                                <button
                                    type="button"
                                    className="em-btn em-btn-primary"
                                    onClick={addScaleOption}
                                >
                                    <Plus size={14} /> {t("Ajouter une valeur")}
                                </button>
                            </div>

                            {formData.scaleOptions.map((option, index) => (
                                <div className="em-scale-row" key={index}>
                                    <div className="em-grid">
                                        <div className="em-field">
                                            <label>{t("Libellé")}</label>
                                            <input
                                                value={option.label}
                                                onChange={(e) =>
                                                    handleScaleOptionChange(index, "label", e.target.value)
                                                }
                                                placeholder={t("Ex : Très satisfait")}
                                            />
                                        </div>

                                        <div className="em-field">
                                            <label>{t("Valeur")}</label>
                                            <input
                                                type="number"
                                                value={option.value}
                                                onChange={(e) =>
                                                    handleScaleOptionChange(
                                                        index,
                                                        "value",
                                                        Number(e.target.value)
                                                    )
                                                }
                                            />
                                        </div>

                                        <div className="em-field">
                                            <label>{t("Position")}</label>
                                            <input
                                                type="number"
                                                value={option.position}
                                                onChange={(e) =>
                                                    handleScaleOptionChange(
                                                        index,
                                                        "position",
                                                        Number(e.target.value)
                                                    )
                                                }
                                            />
                                        </div>

                                        <div className="em-field">
                                            <label>{t("Supprimer")}</label>
                                            <button
                                                type="button"
                                                className="em-link-btn danger em-link-btn-box"
                                                onClick={() => removeScaleOption(index)}
                                                disabled={formData.scaleOptions.length <= 2}
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="em-modal-actions">
                        <button
                            type="button"
                            className="em-btn em-btn-outline"
                            onClick={onClose}
                            disabled={loading}
                        >
                            {t("Annuler")}
                        </button>

                        <button
                            type="submit"
                            className="em-btn em-btn-primary"
                            disabled={loading}
                        >
                            {loading
                                ? t("Chargement...")
                                : editingEvaluation
                                    ? t("Enregistrer")
                                    : t("Créer l'évaluation")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/* ──────────────────────────────────────────────
   Main
────────────────────────────────────────────── */
const EvaluationsManagement = ({
                                   evaluations,
                                   loading,
                                   message,
                                   error,
                                   getAllEvaluations,
                                   getEvaluationsByType,
                                   createEvaluation,
                                   updateEvaluationAction,
                                   deleteEvaluation,
                                   changeStatus,
                                   setCurrentEvaluation,
                               }) => {
    const { t } = useTranslation();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [editingEvaluation, setEditingEvaluation] = useState(null);
    const [previewEvaluation, setPreviewEvaluation] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");



    // Dans la section des states, après selectedEvaluationIds :

    useEffect(() => {
        getAllEvaluations();
    }, [getAllEvaluations]);

    const openCreate = () => {
        setEditingEvaluation(null);
        setCurrentEvaluation(null);
        setIsModalOpen(true);
    };

    const openEdit = (evaluation) => {
        setEditingEvaluation(evaluation);
        setCurrentEvaluation(evaluation);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setEditingEvaluation(null);
        setCurrentEvaluation(null);
        setIsModalOpen(false);
    };

    const openPreview = (evaluation) => {
        setPreviewEvaluation(evaluation);
        setIsPreviewOpen(true);
    };

    const closePreview = () => {
        setPreviewEvaluation(null);
        setIsPreviewOpen(false);
    };

    const handleSubmit = (formData) => {
        if (editingEvaluation) {
            updateEvaluationAction(editingEvaluation.id, formData);
        } else {
            createEvaluation(formData);
        }
        closeModal();
    };

    const handleDelete = (evaluationId, evaluationName) => {
        Swal.fire({
            title: t("Supprimer"),
            text: `${t("Supprimer l'évaluation")} "${evaluationName}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ee645f",
            cancelButtonColor: "#6b7280",
            confirmButtonText: t("Oui, supprimer"),
            cancelButtonText: t("Annuler"),
        }).then((res) => {
            if (res.isConfirmed) {
                deleteEvaluation(evaluationId);
            }
        });
    };

    const handleStatusToggle = (evaluation) => {
        const nextStatus =
            evaluation.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        changeStatus(evaluation.id, nextStatus);
    };

    const handleFilterClick = (filterKey) => {
        setActiveFilter(filterKey);

        if (filterKey === "all") {
            getAllEvaluations();
        } else if (
            filterKey === "HOT" ||
            filterKey === "COLD" ||
            filterKey === "TECHNICAL" ||
            filterKey === "MANAGERIAL" ||
            filterKey === "CUSTOM"
        ) {
            getEvaluationsByType(filterKey);
        }
    };

    const filteredEvaluations = useMemo(() => {
        return (evaluations || []).filter((evaluation) => {
            if (evaluation.typeCode !== "HOT" && evaluation.typeCode !== "COLD") return false;

            const q = searchTerm.toLowerCase().trim();
            if (!q) return true;

            const code = (evaluation.code || "").toLowerCase();
            const name = (evaluation.name || "").toLowerCase();
            const description = (evaluation.description || "").toLowerCase();
            const type = (evaluation.typeCode || "").toLowerCase();

            return (
                code.includes(q) ||
                name.includes(q) ||
                description.includes(q) ||
                type.includes(q)
            );
        });
    }, [evaluations, searchTerm]);

    return (
        <div className="em-page">
            <div className="em-header">
                <div>
                    <h2>{t("Gestion des évaluations")}</h2>

                </div>

                <div className="em-header-actions">
                    <button
                        className="em-btn em-btn-primary"
                        onClick={openCreate}
                        type="button"
                    >
                        + {t("Ajouter une évaluation")}
                    </button>
                </div>
            </div>

            <div className="em-toolbar">
                <input
                    className="em-search"
                    placeholder={t("Rechercher (code, nom, type...)")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />

                <div className="em-filter-tabs">
                    {[
                        { key: "all", label: t("Tous") },
                        { key: "HOT", label: "à chaud" },
                        { key: "COLD", label: "à froid" },
                        // { key: "TECHNICAL", label: "TECHNICAL" },
                        // { key: "MANAGERIAL", label: "MANAGERIAL" },
                        // { key: "CUSTOM", label: "CUSTOM" },
                    ].map((filter) => (
                        <button
                            key={filter.key}
                            className={`em-filter-tab ${
                                activeFilter === filter.key ? "active" : ""
                            }`}
                            onClick={() => handleFilterClick(filter.key)}
                            type="button"
                        >
                            {filter.label}
                        </button>
                    ))}
                </div>


            </div>

            {message && <div className="em-alert em-alert-success">{message}</div>}
            {error && <div className="em-alert em-alert-danger">{error}</div>}

            <div className="em-table-wrapper">
                {loading ? (
                    <div className="em-loading">
                        <div className="em-spinner" />
                        <span>{t("Chargement...")}</span>
                    </div>
                ) : filteredEvaluations.length === 0 ? (
                    <div className="em-empty">
                        <p>{t("Aucune évaluation trouvée")}</p>
                    </div>
                ) : (
                    <table className="em-table">
                        <thead>
                        <tr>
                            <th>{t("Code")}</th>
                            <th>{t("Nom")}</th>
                            <th>{t("Type")}</th>
                            <th>{t("Questions")}</th>
                            <th>{t("Statut")}</th>
                            <th>{t("Date création")}</th>
                            <th style={{ width: 180, textAlign: "right" }}>{t("Actions")}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredEvaluations.map((evaluation) => (
                            <tr key={evaluation.id}>
                                <td>
                                    <span className="em-code">{evaluation.code}</span>
                                </td>

                                <td>
                                    <div className="em-name">{evaluation.name}</div>
                                    {/*<div className="em-desc">*/}
                                    {/*    {evaluation.description || <span className="em-muted">—</span>}*/}
                                    {/*</div>*/}
                                </td>

                                <span className="em-type-badge">
    {evaluation.typeCode === "HOT" ? " Evaluation à chaud" : evaluation.typeCode === "COLD" ? " Evaluation à froid" : evaluation.typeCode}
</span>

                                <td>{evaluation.totalQuestions ?? 0}</td>

                                <td>
                                    <button
                                        type="button"
                                        className={`em-toggle ${
                                            evaluation.status === "ACTIVE" ? "on" : "off"
                                        }`}
                                        onClick={() => handleStatusToggle(evaluation)}
                                        title={t("Changer le statut")}
                                    >
                                        <span className="em-toggle-knob" />
                                        <span>{evaluation.status}</span>
                                    </button>
                                </td>

                                <td className="em-date">
                                    {evaluation.createdAt
                                        ? new Date(evaluation.createdAt).toLocaleDateString("fr-FR")
                                        : "—"}
                                </td>

                                <td className="em-row-actions">
                                    <button
                                        className="em-link-btn"
                                        onClick={() => openPreview(evaluation)}
                                        title={t("Aperçu")}
                                        type="button"
                                    >
                                        <Eye size={15} />
                                    </button>

                                    <button
                                        className="em-link-btn"
                                        onClick={() => openEdit(evaluation)}
                                        title={t("Modifier")}
                                        type="button"
                                    >
                                        <Pencil size={15} />
                                    </button>

                                    <button
                                        className="em-link-btn danger"
                                        onClick={() =>
                                            handleDelete(evaluation.id, evaluation.name)
                                        }
                                        title={t("Supprimer")}
                                        type="button"
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

            <EvaluationModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                editingEvaluation={editingEvaluation}
                loading={loading}
            />

            <EvaluationPreviewModal
                isOpen={isPreviewOpen}
                onClose={closePreview}
                evaluation={previewEvaluation}
                t={t}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    evaluations: state.evaluations?.evaluations || [],
    loading: state.evaluations?.loading,
    message: state.evaluations?.message,
    error: state.evaluations?.error,
});

const mapDispatchToProps = (dispatch) => ({
    getAllEvaluations: () => dispatch(getAllEvaluationsRequest()),
    getEvaluationsByType: (typeCode) =>
        dispatch(getEvaluationsByTypeRequest(typeCode)),
    createEvaluation: (data) => dispatch(createEvaluationRequest(data)),
    updateEvaluationAction: (id, data) =>
        dispatch(updateEvaluationRequest(id, data)),
    deleteEvaluation: (id) => dispatch(deleteEvaluationRequest(id)),
    changeStatus: (id, status) =>
        dispatch(changeEvaluationStatusRequest(id, status)),
    setCurrentEvaluation: (evaluation) => dispatch(updateEvaluation(evaluation)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(EvaluationsManagement);
